// Token Provider — Authentication state with HttpOnly cookies.
//
// Responsibilities:
//   * Bootstraps user/org state via GET /auth/me on mount (no token
//     parsing on the client; the cookie IS the credential).
//   * Tracks an estimated ``tokenExpiresAt`` from /auth/me + login +
//     refresh responses so we can preemptively refresh before the
//     access cookie expires.
//   * Calls /auth/refresh and /auth/logout with empty bodies — the
//     backend reads tokens from cookies.
//   * Wraps API calls with a refresh-on-401-then-retry behaviour.

import {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { toast } from "sonner";
import { setDatadogUser, clearDatadogUser } from "@/lib/datadog";
import {
  authApi,
  type MeResponse,
  type OrganizationInfo,
  type UserInfo,
} from "@/api/services";
import envConfig from "@/config/env";

// Buffer time before token expiry to trigger refresh (30 seconds)
const REFRESH_BUFFER_MS = 30 * 1000;

// Conservative default — used only until /auth/me returns a real value.
// Matches the design doc's si_at TTL (2 hours).
const DEFAULT_ACCESS_TTL_SECONDS = 2 * 60 * 60;

interface TokenContextType {
  // Token state
  isTokenLoaded: boolean;
  isAuthenticated: boolean;
  isTokenExpired: boolean;
  tokenExpiresAt: number | null;

  // User and Organization data
  user: UserInfo | null;
  organization: OrganizationInfo | null;

  // Auth functions
  login: (email: string, password: string, otp?: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  refreshUser: () => Promise<boolean>;

}

const TokenContext = createContext<TokenContextType>({
  isTokenLoaded: false,
  isAuthenticated: false,
  isTokenExpired: false,
  tokenExpiresAt: null,
  user: null,
  organization: null,
  login: async () => false,
  logout: async () => {},
  refreshToken: async () => false,
  refreshUser: async () => false,
});

export function useTokenContext() {
  return useContext(TokenContext);
}

// Simplified hook for components
export function useApiToken() {
  const context = useTokenContext();

  return {
    isTokenLoaded: context.isTokenLoaded,
    isAuthenticated: context.isAuthenticated,
    isTokenExpired: context.isTokenExpired,
    tokenExpiresAt: context.tokenExpiresAt,
    user: context.user,
    organization: context.organization,
    login: context.login,
    logout: context.logout,
    refreshToken: context.refreshToken,
    refreshUser: context.refreshUser,
    isMembershipDataReady: context.isTokenLoaded && context.isAuthenticated,
  };
}

interface TokenProviderProps {
  children: ReactNode;
}


export function TokenProvider({ children }: TokenProviderProps) {
  const [isTokenLoaded, setIsTokenLoaded] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<number | null>(null);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [organization, setOrganization] = useState<OrganizationInfo | null>(
    null,
  );

  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isRefreshingRef = useRef(false);

  /**
   * Calculate token expiration time from expires_in (seconds)
   */
  const calculateExpiresAt = useCallback((expiresIn: number): number => {
    return Date.now() + expiresIn * 1000;
  }, []);

  /**
   * Check if token is expired or will expire soon
   */
  const checkTokenExpiration = useCallback(
    (expiresAt: number | null): boolean => {
      if (!expiresAt) return true;
      return Date.now() >= expiresAt - REFRESH_BUFFER_MS;
    },
    [],
  );

  /**
   * Fetch current user + organisation from the backend.
   *
   * This doubles as the bootstrap check: a successful response proves the
   * si_at cookie is valid, so we flip isAuthenticated=true. A 401 means
   * there's no live session — caller treats it as not-logged-in.
   */
  const fetchUserInfo = useCallback(async (): Promise<boolean> => {
    try {
      const response: MeResponse = await authApi.getMe();
      setUser(response.user);
      setOrganization(response.organization);
      // A successful /me response proves the si_at cookie is valid.
      // Mark the session as authenticated so ProtectedRoute lets the
      // user through — critical for the SSO callback path where
      // refreshUser() is called after the cookie is freshly set.
      setIsAuthenticated(true);
      setDatadogUser({
        id: response.user.keycloak_id,
        email: response.user.email,
        name: `${response.user.first_name} ${response.user.last_name}`,
        role: response.user.role,
        orgId: response.organization.id,
        orgName: response.organization.name,
        orgSlug: response.organization.slug,
      });
      return true;
    } catch (error: any) {
      // 401/403 → no live session. Caller handles UI.
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      console.error("Failed to fetch user info:", error);
      return false;
    }
  }, []);

  /**
   * Rotate the session cookies. The refresh token is read by the backend
   * from the si_rt cookie — no token argument here. Cookies are rotated
   * via Set-Cookie on the response; the browser owns storage and
   * JavaScript never touches the tokens.
   */
  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (isRefreshingRef.current) {
      return false; // Prevent concurrent refresh attempts
    }

    isRefreshingRef.current = true;

    try {
      const response = await authApi.refreshToken();

      const expiresAt = calculateExpiresAt(response.expires_in);
      setTokenExpiresAt(expiresAt);
      setIsTokenExpired(false);
      setIsAuthenticated(true);

      return true;
    } catch (error: any) {
      console.error("Token refresh failed:", error);
      if (error?.status === 401 || error?.status === 403) {
        setIsAuthenticated(false);
        setUser(null);
        setOrganization(null);
      }
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, [calculateExpiresAt]);

  /**
   * Password login. The backend sets si_at + si_rt cookies via Set-Cookie
   * headers; the response body carries only ``expires_in`` and minimal
   * user info. Full user/org details come from a follow-up /auth/me call.
   */
  const login = useCallback(
    async (email: string, password: string, otp?: string): Promise<boolean> => {
      try {
        const response = await authApi.login(email, password, otp);

        const expiresAt = calculateExpiresAt(response.expires_in);
        setTokenExpiresAt(expiresAt);
        setIsTokenExpired(false);
        setIsAuthenticated(true);

        // Fetch full user info after successful login.
        await fetchUserInfo();
        return true;
      } catch (error: any) {
        console.error("Login failed:", error);
        const errorMessage =
          error?.message || "Login failed. Please check your credentials.";
        toast.error(errorMessage);
        return false;
      }
    },
    [calculateExpiresAt, fetchUserInfo],
  );

  /**
   * Logout — back-channel only (mirrors main): the backend reads the refresh
   * token from the si_rt cookie, revokes it (terminating the Keycloak session
   * server-side), and responds with cookie-clear headers so the browser drops
   * si_at/si_rt/si_it. We then clear local React state and ProtectedRoute
   * redirects to /login. We deliberately do NOT follow any Keycloak
   * RP-Initiated Logout URL — emitting a post_logout_redirect_uri would make
   * Keycloak validate it against each realm's post-logout allowlist (non-SSO
   * realms don't configure it → 'Invalid redirect uri'). Same for SSO and
   * password sessions.
   */
  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (error) {
      // Ignore — backend always clears cookies and we reset local state
      // below regardless.
      console.warn("Logout request failed:", error);
    }

    // Clear local state so ProtectedRoute can redirect to /login.
    setIsAuthenticated(false);
    setIsTokenExpired(false);
    setTokenExpiresAt(null);
    setUser(null);
    setOrganization(null);
    clearDatadogUser();

    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, []);

  /**
   * Periodic preemptive refresh. Runs every 30s and triggers a refresh
   * whenever the estimated expiry is within the buffer window.
   */
  const setupTokenRefresh = useCallback(
    (expiresAt: number | null) => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }

      if (!expiresAt || !isAuthenticated) return;

      const timeToRefresh = Math.max(
        1000,
        expiresAt - Date.now() - REFRESH_BUFFER_MS,
      );

      const timeoutId = setTimeout(() => {
        refreshToken();
      }, timeToRefresh);

      const intervalId = setInterval(() => {
        if (checkTokenExpiration(tokenExpiresAt)) {
          setIsTokenExpired(true);
          refreshToken();
        }
      }, 30000);

      refreshIntervalRef.current = intervalId;

      return () => {
        clearTimeout(timeoutId);
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
      };
    },
    [isAuthenticated, refreshToken, checkTokenExpiration, tokenExpiresAt],
  );

  // Bootstrap on mount: ask the backend whether we're logged in.
  // /auth/me succeeds → si_at cookie was valid → we're authenticated.
  // /auth/me 401s → no live session; the SSO/login UI handles it.
  useEffect(() => {
    if (envConfig.enableMockApi) {
      // Mock mode: auto-login so the app is usable without a real backend.
      // (Cookie model: we never read stale localStorage tokens — the #166
      // "don't reuse stale tokens" concern is moot here.)
      login("demo@stormimpact.dev", "mock").finally(() => {
        setIsTokenLoaded(true);
      });
      return;
    }

    fetchUserInfo().then((ok) => {
      if (ok) {
        // We have a valid session. We don't know the exact remaining TTL
        // (it's in the HttpOnly cookie the browser holds), so seed the
        // estimate with the default access TTL — a refresh-on-401 will
        // correct any drift.
        setTokenExpiresAt(calculateExpiresAt(DEFAULT_ACCESS_TTL_SECONDS));
        setIsAuthenticated(true);
      }
      setIsTokenLoaded(true);
    });
  }, [fetchUserInfo, calculateExpiresAt, login]);

  // Set up automatic refresh when token expiration changes.
  useEffect(() => {
    const cleanup = setupTokenRefresh(tokenExpiresAt);
    return cleanup;
  }, [tokenExpiresAt, setupTokenRefresh]);

  // Cleanup intervals on unmount.
  useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  // Update expired status when token expiration changes.
  useEffect(() => {
    setIsTokenExpired(checkTokenExpiration(tokenExpiresAt));
  }, [tokenExpiresAt, checkTokenExpiration]);

  const value: TokenContextType = {
    isTokenLoaded,
    isAuthenticated,
    isTokenExpired,
    tokenExpiresAt,
    user,
    organization,
    login,
    logout,
    refreshToken,
    refreshUser: fetchUserInfo,
  };

  return (
    <TokenContext.Provider value={value}>{children}</TokenContext.Provider>
  );
}

// MockTokenProvider — kept for backwards compatibility / testing.
export function MockTokenProvider({ children }: TokenProviderProps) {
  const value: TokenContextType = {
    isTokenLoaded: true,
    isAuthenticated: true,
    isTokenExpired: false,
    tokenExpiresAt: Date.now() + 3600000, // 1 hour from now
    user: {
      keycloak_id: "demo-user-001",
      email: "demo@example.com",
      first_name: "Demo",
      last_name: "User",
      role: "user",
      roles: ["user"],
      has_local_password: true,
    },
    organization: {
      id: "demo-org-001",
      name: "Demo Organization",
      slug: "demo-org",
      auth_mode: "password_only",
    },
    login: async () => true,
    logout: async () => {},
    refreshToken: async () => true,
    refreshUser: async () => true,
  };

  return (
    <TokenContext.Provider value={value}>{children}</TokenContext.Provider>
  );
}
